import React, { useCallback, useMemo, useState } from 'react';
import { ExpandMore, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { DocumentGenerationPayloadEntity } from '@/types/DocumentGenerationPayload';
import type { Entity } from '@/types/Entity';
import { useEntities } from '@/hooks/queries/entities/useEntities';
import { useRegenerateDocument } from '@/hooks/mutations/documents/useRegenerateDocument';
import { preOpenWindow, openBase64PdfInWindow, openDocumentWithAuth } from '@/utils/pdfHelpers';
import { getErrorMessage } from '@/utils/httpClient';

export interface DocumentPayloadsSectionProps {
  transactionId: string;
  /** Document numbers from the transaction's assets, used to find payloads
   *  created before the transaction existed (transaction_id was null at save time). */
  documentNumbers?: string[];
  /** Related entity IDs (e.g. loss record IDs) whose payloads should also match. */
  relatedEntityIds?: string[];
  formatDateTime: (value: string | null | undefined) => string;
}

/**
 * Build a PostgREST `or` filter to find payload entities linked to this transaction.
 *
 * Payloads saved during the issue workflow may not have `transaction_id` set
 * because the transaction entity is created AFTER document generation.
 * We also match by `non_expendable_document_number` or `expendable_document_number`
 * which ARE populated at generation time.
 */
/** Quote a value for use in a PostgREST `or(...)` filter if it contains
 *  characters that would be misinterpreted (commas, parentheses, dots). */
const pgQuote = (value: string): string => {
  if (/[,()."\\]/.test(value)) {
    return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return value;
};

const buildOrFilter = (
  transactionId: string,
  documentNumbers: string[],
  relatedEntityIds: string[],
): string => {
  const conditions = [`entity_data->>transaction_id.eq.${pgQuote(transactionId)}`];

  for (const num of documentNumbers) {
    const quoted = pgQuote(num);
    conditions.push(`entity_data->>non_expendable_document_number.eq.${quoted}`);
    conditions.push(`entity_data->>expendable_document_number.eq.${quoted}`);
    conditions.push(`entity_data->>document_number.eq.${quoted}`);
  }

  for (const id of relatedEntityIds) {
    const quoted = pgQuote(id);
    conditions.push(`entity_data->>related_entity_id.eq.${quoted}`);
    conditions.push(`entity_data->payload->>relatedEntityId.eq.${quoted}`);
  }

  return `(${conditions.join(',')})`;
};

export const DocumentPayloadsSection: React.FC<DocumentPayloadsSectionProps> = ({
  transactionId,
  documentNumbers = [],
  relatedEntityIds = [],
  formatDateTime,
}) => {
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [expandedPayloadId, setExpandedPayloadId] = useState<string | null>(null);

  const orFilter = useMemo(
    () => buildOrFilter(transactionId, documentNumbers, relatedEntityIds),
    [transactionId, documentNumbers, relatedEntityIds],
  );

  const {
    data: rawPayloads,
    isLoading,
    isError,
    error: fetchError,
  } = useEntities({
    type: 'document_generation_payload',
    filters: { or: orFilter },
    order: 'created_at.desc',
    enabled: Boolean(transactionId),
  });

  const payloads = (rawPayloads ?? []) as DocumentGenerationPayloadEntity[];
  const regenerateMutation = useRegenerateDocument();

  const handleRegenerate = useCallback(
    (entity: DocumentGenerationPayloadEntity) => {
      const data = entity.entity_data;

      // Pre-open window synchronously in click handler to avoid popup blocker
      const pdfWindow = preOpenWindow();

      setRegeneratingId(entity.id);
      setRegenerateError(null);

      regenerateMutation.mutate(
        {
          documentType: data.document_type,
          payload: data.payload,
          options: data.options ?? undefined,
          transactionId,
          nonExpendableDocumentNumber: data.non_expendable_document_number ?? undefined,
          expendableDocumentNumber: data.expendable_document_number ?? undefined,
        },
        {
          onSuccess: (response) => {
            setRegeneratingId(null);

            if (response.outputBuffer) {
              openBase64PdfInWindow(response.outputBuffer, pdfWindow);
            } else if (response.downloadUrl) {
              void openDocumentWithAuth(response.downloadUrl, pdfWindow);
            } else {
              // No content to display — close the spinner window
              if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
            }
          },
          onError: (err) => {
            setRegeneratingId(null);
            setRegenerateError(getErrorMessage(err));
            if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
          },
        },
      );
    },
    [regenerateMutation],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load document payloads: {getErrorMessage(fetchError)}
      </Alert>
    );
  }

  if (payloads.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No saved document payloads for this transaction.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Saved document payloads can be regenerated to produce a new stored copy of the document.
      </Typography>
      {regenerateError && (
        <Alert severity="error" onClose={() => setRegenerateError(null)}>
          {regenerateError}
        </Alert>
      )}
      {payloads.map((entity: Entity) => {
        const payload = entity as DocumentGenerationPayloadEntity;
        const data = payload.entity_data;
        const isRegenerating = regeneratingId === entity.id;
        const isPayloadExpanded = expandedPayloadId === entity.id;

        return (
          <Box
            key={entity.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Chip
                  size="small"
                  label={data.document_type}
                  variant="outlined"
                  sx={{ textTransform: 'uppercase', fontWeight: 800 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatDateTime(entity.created_at)}
                </Typography>
                {data.non_expendable_document_number && (
                  <Chip size="small" label={`NE#${data.non_expendable_document_number}`} />
                )}
                {data.expendable_document_number && (
                  <Chip size="small" label={`E#${data.expendable_document_number}`} />
                )}
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    setExpandedPayloadId(isPayloadExpanded ? null : entity.id)
                  }
                  startIcon={isPayloadExpanded ? <VisibilityOff /> : <Visibility />}
                >
                  {isPayloadExpanded ? 'Hide' : 'Payload'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isRegenerating}
                  onClick={() => handleRegenerate(payload)}
                  startIcon={
                    isRegenerating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Refresh />
                    )
                  }
                >
                  {isRegenerating ? 'Generating...' : 'Regenerate'}
                </Button>
              </Stack>
            </Box>
            {isPayloadExpanded && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{ mt: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="caption" fontWeight={600}>
                    Raw Payload
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {JSON.stringify(data.payload, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
